import React, { useState, useEffect } from 'react';
import { Card, Alert, Statistic, Row, Col, Tag, Spin } from 'antd';
import { ClockCircleOutlined, TeamOutlined, WarningOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { clinicService } from '../../services';

const SubscriptionInfo = () => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [subscription, setSubscription] = useState(null);

    useEffect(() => {
        fetchSubscription();
    }, []);

    const fetchSubscription = async () => {
        try {
            setLoading(true);
            const response = await clinicService.getSubscription();
            if (response.success) {
                setSubscription(response.data);
            }
        } catch (error) {
            console.error('Failed to fetch subscription:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return <Spin />;
    }

    if (!subscription) {
        return null;
    }

    const getStatusColor = (status) => {
        switch (status) {
            case 'active':
                return 'success';
            case 'trial':
                return 'processing';
            case 'expired':
                return 'error';
            case 'suspended':
                return 'warning';
            default:
                return 'default';
        }
    };

    const getDaysColor = (days) => {
        if (days <= 0) return '#ff4d4f'; // red
        if (days <= 7) return '#faad14'; // yellow
        if (days <= 30) return '#1890ff'; // blue
        return '#52c41a'; // green
    };

    return (
        <Card
            style={{
                marginBottom: 24,
                borderRadius: 12,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}
        >
            {subscription.isExpiringSoon && subscription.hasValidSubscription && (
                <Alert
                    message={t('subscription.expiringSoon', { days: subscription.remainingDays })}
                    description={t('subscription.renewReminder')}
                    type="warning"
                    showIcon
                    icon={<WarningOutlined />}
                    style={{ marginBottom: 16 }}
                />
            )}

            {!subscription.hasValidSubscription && (
                <Alert
                    message={t('subscription.expired')}
                    description={t('subscription.expiredMessage')}
                    type="error"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
            )}

            <Row gutter={16}>
                <Col xs={24} sm={12} md={6}>
                    <Card bordered={false}>
                        <Statistic
                            title={t('subscription.status')}
                            value={subscription.subscriptionStatus.toUpperCase()}
                            prefix={<CheckCircleOutlined />}
                            valueStyle={{
                                fontSize: 18,
                                color: getStatusColor(subscription.subscriptionStatus) === 'success' ? '#52c41a' :
                                    getStatusColor(subscription.subscriptionStatus) === 'error' ? '#ff4d4f' :
                                        getStatusColor(subscription.subscriptionStatus) === 'warning' ? '#faad14' : '#1890ff'
                            }}
                        />
                        <Tag
                            color={getStatusColor(subscription.subscriptionStatus)}
                            style={{ marginTop: 8 }}
                        >
                            {subscription.subscriptionStatus}
                        </Tag>
                    </Card>
                </Col>

                <Col xs={24} sm={12} md={6}>
                    <Card bordered={false}>
                        <Statistic
                            title={t('subscription.daysRemaining')}
                            value={subscription.remainingDays}
                            prefix={<ClockCircleOutlined />}
                            suffix={t('subscription.days')}
                            valueStyle={{
                                fontSize: 24,
                                color: getDaysColor(subscription.remainingDays)
                            }}
                        />
                    </Card>
                </Col>

                <Col xs={24} sm={12} md={6}>
                    <Card bordered={false}>
                        <Statistic
                            title={t('subscription.dentists')}
                            value={`${subscription.currentDentistCount} / ${subscription.dentistLimit}`}
                            prefix={<TeamOutlined />}
                            valueStyle={{ fontSize: 24 }}
                        />
                        {!subscription.canAddDentist && (
                            <Tag color="error" style={{ marginTop: 8 }}>
                                {t('subscription.limitReached')}
                            </Tag>
                        )}
                    </Card>
                </Col>

                <Col xs={24} sm={12} md={6}>
                    <Card bordered={false}>
                        <Statistic
                            title={t('subscription.endDate')}
                            value={new Date(subscription.subscriptionEndDate).toLocaleDateString()}
                            valueStyle={{ fontSize: 18 }}
                        />
                    </Card>
                </Col>
            </Row>

            {subscription.yearlyAmount > 0 && (
                <Row gutter={16} style={{ marginTop: 16 }}>
                    <Col span={12}>
                        <Card bordered={false} style={{ background: '#f0f2f5' }}>
                            <Statistic
                                title={t('subscription.yearlyAmount')}
                                value={subscription.yearlyAmount}
                                precision={2}
                                prefix="$"
                                valueStyle={{ fontSize: 18 }}
                            />
                        </Card>
                    </Col>
                    {subscription.lastPaymentDate && (
                        <Col span={12}>
                            <Card bordered={false} style={{ background: '#f0f2f5' }}>
                                <Statistic
                                    title={t('subscription.lastPayment')}
                                    value={subscription.lastPaymentAmount}
                                    precision={2}
                                    prefix="$"
                                    valueStyle={{ fontSize: 18 }}
                                />
                                <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
                                    {new Date(subscription.lastPaymentDate).toLocaleDateString()}
                                </div>
                            </Card>
                        </Col>
                    )}
                </Row>
            )}
        </Card>
    );
};

export default SubscriptionInfo;
